import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart as ReLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import { LineChartBlock as LineChartBlockType } from '../../viz.types';
import VizAxisLabel from '../VizAxisLabel';
import VizBlockFrame from '../VizBlockFrame';
import VizRangeSelector from '../VizRangeSelector';
import VizTotal from '../VizTotal';
import LineEndLabel from './LineEndLabel';
import { formatDeltaRatio } from './lineChartBlock.utils';
import { useLineChartBlock } from './useLineChartBlock';

interface LineChartBlockProps {
  block: LineChartBlockType;
}

const CHART_HEIGHT = 220;
const LINE_STROKE_WIDTH = 2;
const GRID_DASH = '4 4';

const LineChartBlock = ({ block }: LineChartBlockProps) => {
  const { translate } = useTranslation();
  const {
    formatters,
    chartTheme,
    summary,
    gradientId,
    chartMargin,
    data,
    ranges,
    selectedIndex,
    selectRange,
  } = useLineChartBlock(block);

  return (
    <VizBlockFrame
      title={block.title}
      subtitle={block.subtitle}
      actions={
        ranges.length > 1 ? (
          <VizRangeSelector ranges={ranges} selectedIndex={selectedIndex} onSelect={selectRange} />
        ) : null
      }
      header={
        summary && (
          <VizTotal value={formatters.formatCompact(summary.value)} caption={summary.caption}>
            {summary.deltaRatio !== null && (
              <Typography
                variant="micro1"
                sx={{
                  color: summary.deltaRatio < 0 ? 'custom.status.error' : 'custom.status.success',
                }}
              >
                {translate(LangEnum.RACHEL_CHART_PERIOD_DELTA, {
                  delta: formatDeltaRatio(summary.deltaRatio, formatters.locale),
                })}
              </Typography>
            )}
          </VizTotal>
        )
      }
    >
      <Stack direction="row" sx={{ gap: 1, minWidth: 0 }}>
        {block.config?.yLabel && <VizAxisLabel isVertical>{block.config.yLabel}</VizAxisLabel>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ReLineChart data={data} margin={chartMargin}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={chartTheme.lineGradient.from} />
                  <stop offset="100%" stopColor={chartTheme.lineGradient.to} />
                </linearGradient>
              </defs>
              <CartesianGrid
                vertical={false}
                strokeDasharray={GRID_DASH}
                stroke={chartTheme.gridColor}
              />
              <XAxis
                dataKey="label"
                height="auto"
                tick={{ fontSize: chartTheme.axisFontSize, fill: chartTheme.axisColor }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatters.formatPointLabel}
              />
              <YAxis
                tick={{ fontSize: chartTheme.axisFontSize, fill: chartTheme.axisColor }}
                tickLine={false}
                axisLine={false}
                width="auto"
                domain={['auto', 'auto']}
                tickFormatter={formatters.formatCompact}
              />
              <Tooltip
                {...chartTheme.tooltipStyles}
                separator=""
                labelFormatter={(label) => formatters.formatPointLabel(String(label))}
                formatter={(v) => [formatters.formatFull(Number(v)), '']}
              />
              <Line
                type="monotone"
                dataKey="value"
                strokeWidth={LINE_STROKE_WIDTH}
                dot={false}
                stroke={`url(#${gradientId})`}
              >
                <LabelList dataKey="endLabel" content={<LineEndLabel />} />
              </Line>
            </ReLineChart>
          </ResponsiveContainer>
          {block.config?.xLabel && (
            <Stack alignItems="center">
              <VizAxisLabel>{block.config.xLabel}</VizAxisLabel>
            </Stack>
          )}
        </Box>
      </Stack>
    </VizBlockFrame>
  );
};

export default LineChartBlock;
